import { useState } from "react";

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d7b464uoim692ipjlucg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="w-full py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-px bg-gray-400" />
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">Contact</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            Get in Touch
          </h2>
          <p className="text-gray-400 text-base max-w-xs leading-relaxed">
            Have a question or want to learn more? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className="flex flex-col gap-8">
            <div>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                INDOS d.o.o. is based in Bosnia and Herzegovina. Whether you have a question about our products, need technical support, or want to explore a partnership, our team is ready to help.
              </p>
            </div>

            {/* Address Block */}
            <div className="bg-gray-50 rounded-2xl p-7 flex flex-col gap-5">
              <h3 className="text-gray-900 font-semibold text-sm uppercase tracking-widest">Company Information</h3>

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-50 shrink-0">
                  <i className="ri-map-pin-line text-sky-500 text-base" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Address</p>
                  <p className="text-gray-800 text-sm font-medium">Ivanica bb</p>
                  <p className="text-gray-600 text-sm">City: Ravno</p>
                  <p className="text-gray-600 text-sm">ZIP: 88370</p>
                  <p className="text-gray-600 text-sm">Country: Bosnia and Herzegovina</p>
                </div>
              </div>

              <div className="w-full h-px bg-gray-200" />

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-50 shrink-0">
                  <i className="ri-mail-line text-sky-500 text-base" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Email</p>
                  <p className="text-gray-800 text-sm font-medium">contact@indosdoo.com</p>
                </div>
              </div>

              <div className="w-full h-px bg-gray-200" />

              <div className="flex items-start gap-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-50 shrink-0">
                  <i className="ri-phone-line text-sky-500 text-base" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Phone Number</p>
                  <p className="text-gray-800 text-sm font-medium">+1 6066991508</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-sky-400 transition-colors bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-sky-400 transition-colors bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us how we can help..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-sky-400 transition-colors bg-gray-50 resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount >= 500 ? "text-red-400" : "text-gray-400"}`}>
                  {charCount}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={status === "sending" || charCount > 500}
                className="bg-gray-900 text-white font-semibold text-sm px-8 py-3.5 rounded-xl cursor-pointer hover:bg-gray-700 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <i className="ri-checkbox-circle-line text-emerald-500" />
                  <p className="text-emerald-700 text-sm font-medium">Message sent successfully! We&apos;ll be in touch soon.</p>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line text-red-500" />
                  <p className="text-red-700 text-sm font-medium">Something went wrong. Please try again.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Footer Bar */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/97ffa25b-3780-4a1d-b051-881c188f3c2b.png"
            alt="INDOS d.o.o."
            className="h-7 w-auto object-contain"
          />
          <span className="text-gray-500 text-sm font-medium">INDOS d.o.o.</span>
        </div>
        <p className="text-gray-400 text-xs text-center">
          &copy; {new Date().getFullYear()} INDOS d.o.o. All rights reserved. iOS Development &bull; Mobile Security &bull; App Optimization
        </p>
      </div>
    </section>
  );
}
